import { useEffect, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Box, Typography } from '@mui/material';
import { QuoteGetResponse } from '@lio/api-types';
import { useInsertNote } from '@services/note/useInsertNote';
import { useUpdateQuote } from '@services/quotes';
import { TextField } from '@components/textField/TextField';
import { ConfirmationDialog } from '@components/dialog';
import { Checkbox } from '@components/checkbox/Checkbox';
import { FormControlLabel } from '@components/formControlLabel/FormControlLabel';
import { ToastType } from '@components/toast';
import { NoteType } from '@models/note';

interface Props {
  isOpen: boolean;
  policyNumber: string;
  onClose: () => void;
  quote: QuoteGetResponse;
}

export const ReleaseQuoteDialog = ({ isOpen, policyNumber, onClose, quote }: Props) => {
  const [message, setMessage] = useState('');
  const [sendRenewalReleasedEmail, setSendRenewalReleasedEmail] = useState(false);
  const isRenewalQuote = quote.quoteType === 'Renewal';

  const { updateQuote, isPending } = useUpdateQuote();
  const { insertNoteAsync } = useInsertNote();

  useEffect(() => {
    if (!isOpen) {
      setSendRenewalReleasedEmail(false);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    try {
      await updateQuote({
        quoteId: quote.id,
        hasInternalOnlyAccessRestriction: false,
        ...(isRenewalQuote && sendRenewalReleasedEmail ? { sendRenewalReleasedEmail: true } : {}),
      }).then(() => {
        if (message !== '') {
          const insertMessageBody = {
            type: NoteType.Message,
            body: message,
            rawText: message,
            quoteId: quote.id,
            customerId: quote.customer.id,
            brokerageId: quote.brokerage.id,
          };
          insertNoteAsync({ noteRequestBody: insertMessageBody });
        }
        onClose();
        enqueueSnackbar(`Quote was successfully released.`, {
          preventDuplicate: true,
          variant: ToastType.Success,
        });
      });
    } catch {
      enqueueSnackbar(`Release of quote has failed.`, {
        preventDuplicate: true,
        variant: ToastType.Error,
        persist: true,
      });
    }
  };

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onCancel={onClose}
      title={`Release Quote ${policyNumber}`}
      dialogPadding='0px'
      showBorders
      sxProps={{
        titleContainerSx: {
          py: 1,
          paddingLeft: 2,
          paddingRight: 1,
          minHeight: 'unset',
          height: 'unset',
          flexWrap: 'nowrap',
        },
        title: {
          marginLeft: 0,
          marginTop: 0,
          padding: 0,
        },
        checkboxListContainer: {
          px: 2,
        },
        dialogActionsSx: {
          paddingTop: 1.5,
          paddingBottom: 1.5,
        },
        dialogContent: {
          marginBottom: 3,
        },
      }}
      checkboxText={["I'm sure that I want to release this quote to the broker"]}
      isLoading={isPending}
      buttonConfig={{
        alignment: 'right',
        onConfirm: handleSubmit,
        confirmText: `Release Quote`,
        secondaryText: 'Exit',
      }}
    >
      <Box
        sx={{
          px: 2,
          pt: 2,
        }}
      >
        <TextField
          multiline
          fullWidth
          inputLabel='Message to Broker (Optional)'
          helperText='Adding an optional message will send a second email to the broker'
          onChangeInput={(value) => {
            setMessage(value);
          }}
          value={message}
        />
        {isRenewalQuote ? (
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              label={
                <Typography
                  variant='bodySmall'
                  sx={{
                    color: 'text.main',
                  }}
                >
                  Send a Renewal Released email to the broker
                </Typography>
              }
              sx={{ fontSize: 0 }}
              control={
                <Checkbox
                  checked={sendRenewalReleasedEmail}
                  onChange={(event) => {
                    setSendRenewalReleasedEmail(event.target.checked);
                  }}
                  sx={{ paddingLeft: 0 }}
                />
              }
            />
          </Box>
        ) : null}
      </Box>
    </ConfirmationDialog>
  );
};
